/**
 * Учебные треки (docs/06-api.md, раздел «Треки»).
 *
 * Ключи содержат идентификатор смотрящего по той же причине, что и в каталоге:
 * в ответе есть прогресс, и после восстановления сессии данные обязаны
 * перезапроситься сами.
 */

import { useQuery } from '@tanstack/react-query'
import { z } from 'zod'

import { taskProgressSchema, topicRefSchema, useViewerId } from './catalog'
import { apiFetch } from './client'
import { authFetch, getAccessToken } from './session'

export const trackSchema = z.object({
  slug: z.string(),
  title: z.string(),
  description: z.string().nullable(),
  position: z.number(),
  tasks_total: z.number(),
  tasks_solved: z.number().nullable().default(null),
})

export const trackListSchema = z.object({ items: z.array(trackSchema) })

export const trackStepSchema = z.object({
  position: z.number(),
  slug: z.string(),
  title: z.string(),
  topic: topicRefSchema,
  difficulty: z.enum(['easy', 'medium', 'hard']),
  progress: taskProgressSchema.nullable().default(null),
})

export const trackDetailSchema = z.object({
  slug: z.string(),
  title: z.string(),
  description: z.string().nullable(),
  position: z.number(),
  steps: z.array(trackStepSchema),
  tasks_total: z.number(),
  tasks_solved: z.number().nullable().default(null),
  next_task_slug: z.string().nullable().default(null),
})

export type Track = z.infer<typeof trackSchema>
export type TrackStep = z.infer<typeof trackStepSchema>
export type TrackDetail = z.infer<typeof trackDetailSchema>

export const trackKeys = {
  list: (viewer: string | null) => ['tracks', 'list', viewer] as const,
  track: (viewer: string | null, slug: string) => ['tracks', 'track', viewer, slug] as const,
}

/** См. `fetchPublic` в catalog.ts: гостю нельзя ходить через `authFetch`. */
function fetchPublic<T>(path: string): Promise<T> {
  return getAccessToken() ? authFetch<T>(path) : apiFetch<T>(path)
}

export async function fetchTracks(): Promise<Track[]> {
  return trackListSchema.parse(await fetchPublic<unknown>('/tracks')).items
}

export async function fetchTrack(slug: string): Promise<TrackDetail> {
  return trackDetailSchema.parse(await fetchPublic<unknown>(`/tracks/${encodeURIComponent(slug)}`))
}

export function useTracks() {
  const viewer = useViewerId()
  return useQuery({ queryKey: trackKeys.list(viewer), queryFn: fetchTracks })
}

export function useTrack(slug: string, enabled = true) {
  const viewer = useViewerId()
  return useQuery({
    queryKey: trackKeys.track(viewer, slug),
    queryFn: () => fetchTrack(slug),
    enabled: enabled && slug.length > 0,
    retry: false,
  })
}
